import React, {
  useCallback,
  useState,
  useRef,
  ChangeEvent,
  MouseEventHandler,
} from 'react';
import { Popover, Slider } from '@mui/material';
import AvatarEditor from 'react-avatar-editor';
import styled from 'styled-components';
import {
  SPWCancelButton,
  SPWSaveButton,
} from 'spw-shared/globalComponents/buttons';
import { colors } from 'spw-theme';
import RestoreDefaultAvatarPopover from './restore-default-avatar-popover';

const AvatarEditorCard = styled.div`
  display: flex;
  flex-direction: column;
  height: 488px;
  width: 400px;

  > .content {
    flex: 1;
    padding: 16px 16px 0;
  }

  > .footer {
    padding: 16px;

    .MuiButton-root {
      box-sizing: border-box;
      width: 88px;

      &:first-child {
        margin-right: 16px;
      }
    }
  }
`;

const AvatarImg = styled.img`
  border-radius: 4px;
  height: 40px;
  width: 40px;
`;

const UploadableAvatarImgContainer = styled.div`
  display: flex;
  align-items: center;
  object-fit: cover;
  border-radius: 4px;
  position: relative;
  &:hover {
    .delete-avatar-container {
      visibility: inherit;
    }
  }
  .delete-avatar-container {
    visibility: hidden;
    display: flex;
    height: 16px;
    width: 16px;
    position: absolute;
    top: -6px;
    left: 30px;
    background-color: #fff;
    border-radius: 50%;
    justify-content: center;
    object-fit: cover;
    align-items: center;

    img {
      height: 14px;
      width: 14px;
    }
  }
`;

const AvatarImgContainer = styled.div`
  display: flex;
  align-items: center;
  object-fit: cover;
  border-radius: 4px;
`;

const Trigger = styled.button`
  cursor: pointer;
  padding: 0;
  border: none;
  background: transparent;
`;

type Props = {
  alt: string;
  className?: string;
  disableUpload?: boolean;
  noContent: JSX.Element;
  onChange: (base64: string) => void;
  src?: string;
  onClick?: MouseEventHandler;
  dataTestId?: string;
};

const Avatar = ({
  alt,
  className,
  disableUpload,
  noContent,
  onChange,
  src,
  onClick,
  dataTestId,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [image, setImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [imageUploaded, setImageUploaded] = useState(false);
  const hiddenInput = useRef<HTMLInputElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const editorRef = useRef<AvatarEditor>(null);

  const handleTriggerClick = useCallback(() => {
    if (hiddenInput.current) {
      hiddenInput.current.click();
    }
  }, [hiddenInput]);

  const handleImageSelected = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.currentTarget.files.length) {
        setImage(e.currentTarget.files[0]);
        setAnchorEl(triggerRef.current);
      }
    },
    [setImage, setAnchorEl, triggerRef]
  );

  const handleZoomChange = useCallback(
    (_: Event, newValue: number) => {
      setZoom(newValue);
    },
    [setZoom]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleSaveButtonClick = useCallback(() => {
    if (editorRef.current) {
      const canvas = editorRef.current.getImage();

      onChange(canvas.toDataURL());
      setAnchorEl(null);
      setImage(null);
    }
  }, [editorRef, onChange, setAnchorEl, setImage]);

  const handleCancelButtonClick = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleHiddenInputClick = useCallback<
    MouseEventHandler<HTMLInputElement>
  >((e) => {
    e.currentTarget.value = null;
  }, []);

  const content = src ? (
    <AvatarImg src={src} alt={alt} data-testid={dataTestId} />
  ) : (
    noContent
  );

  if (disableUpload) {
    return (
      <AvatarImgContainer
        className={className}
        onClick={onClick}
        role="button"
        aria-hidden="true"
      >
        {content}
      </AvatarImgContainer>
    );
  }

  return (
    <>
      <Trigger
        className={`${className ?? ''}`}
        ref={triggerRef}
        onClick={handleTriggerClick}
        type="button"
      >
        <UploadableAvatarImgContainer>
          {content}
          {src && <RestoreDefaultAvatarPopover onRemove={() => onChange('')} />}
        </UploadableAvatarImgContainer>
      </Trigger>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableScrollLock
      >
        <AvatarEditorCard>
          <div
            className="content"
            data-testid={imageUploaded ? 'avatar-upload-success' : ''}
          >
            <AvatarEditor
              ref={editorRef}
              image={image}
              width={318}
              height={318}
              border={25}
              color={[255, 255, 255, 0.6]}
              scale={zoom}
              rotate={0}
              onLoadSuccess={() => {
                setImageUploaded(true);
              }}
            />
            <Slider
              min={0.5}
              max={2}
              step={0.1}
              value={zoom}
              onChange={handleZoomChange}
            />
          </div>
          <div className="footer">
            <SPWSaveButton onClick={handleSaveButtonClick} />
            <SPWCancelButton
              customColor={colors.black}
              onClick={handleCancelButtonClick}
            />
          </div>
        </AvatarEditorCard>
      </Popover>
      <input
        type="file"
        accept="image/jpeg, image/png"
        ref={hiddenInput}
        onChange={handleImageSelected}
        onClick={handleHiddenInputClick}
        data-testid="upload-avatar"
        style={{ display: 'none' }}
      />
    </>
  );
};

export default Avatar;
